import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface Announcement {
  id: number;
  orgNumber: string;
  companyName: string;
  date: string;
  announcementId: string;
  announcementText: string;
  link: string;
  contactPerson: string;
}

interface AnnouncementsWidgetProps {
  announcements: Announcement[];
  isLoading: boolean;
  maxHeight?: number | null;
}

export const AnnouncementsWidget: React.FC<AnnouncementsWidgetProps> = ({ announcements, isLoading, maxHeight }) => {
  const { user } = useAuth();
  const [filterBy, setFilterBy] = useState<string | null>(null);

  // Filter announcements based on selected filter
  const filteredAnnouncements = useMemo(() => {
    if (!filterBy) {
      return announcements;
    }
    
    return announcements.filter(announcement => {
      if (filterBy === 'my-customers') {
        // Filter by logged-in user's name (case-insensitive)
        if (!user?.name) return false;
        const userName = user.name.toLowerCase();
        const contactPerson = announcement.contactPerson?.toLowerCase() || '';
        return contactPerson.includes(userName) || userName.includes(contactPerson);
      } else {
        // Filter by specific contact person
        const contactPerson = announcement.contactPerson?.toLowerCase() || '';
        return contactPerson.includes(filterBy.toLowerCase());
      }
    });
  }, [announcements, filterBy, user]);

  const handleMyCustomersClick = () => {
    if (filterBy === 'my-customers') {
      setFilterBy(null); // Toggle off
    } else {
      setFilterBy('my-customers');
    }
  };

  const handleContactPersonClick = (contactPerson: string) => {
    if (filterBy === contactPerson) {
      setFilterBy(null); // Toggle off
    } else {
      setFilterBy(contactPerson);
    }
  };

  return (
    <div 
      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/40 overflow-hidden flex flex-col" 
      style={{ 
        maxHeight: maxHeight ? `${maxHeight}px` : '480px', 
        width: '100%' 
      }}
    >
      <div className="px-6 py-4 border-b border-slate-200/40 bg-gradient-to-r from-slate-50/50 to-blue-50/50 flex-shrink-0">
        <h3 className="text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2">
          Announcements
        </h3>
        <button
          onClick={handleMyCustomersClick}
          className={`text-xs font-medium transition-colors ${
            filterBy === 'my-customers'
              ? 'text-blue-600 underline'
              : 'text-blue-600 hover:text-blue-800 hover:underline'
          }`}
        >
          My customers only
        </button>
      </div>
      
      <div 
        className="flex-1 overflow-y-scroll px-6 py-4 custom-scrollbar" 
      >
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-slate-600">Henter kunngjøringer...</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-sm text-slate-600">Ingen kunngjøringer tilgjengelig</p>
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-sm text-slate-600">Ingen kunngjøringer tilgjengelig</p>
            {filterBy && (
              <button
                onClick={() => setFilterBy(null)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Fjern filter
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAnnouncements.map((announcement) => (
              <div 
                key={announcement.id} 
                className="bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-200/60 p-4 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-900 mb-1">
                      {announcement.companyName}
                    </h4>
                    {announcement.date && (
                      <p className="text-xs text-slate-500 mb-2">
                        {announcement.date}
                      </p>
                    )}
                  </div>
                  {announcement.link && (
                    <a
                      href={announcement.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 flex-shrink-0 text-blue-600 hover:text-blue-800 transition-colors"
                      title="Åpne lenke"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
                {announcement.announcementText && (
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {announcement.announcementText}
                  </p>
                )}
                {announcement.contactPerson && (
                  <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                    Kontakt:{' '}
                    <button
                      onClick={() => handleContactPersonClick(announcement.contactPerson)}
                      className={`font-medium transition-colors ${
                        filterBy === announcement.contactPerson
                          ? 'text-blue-600 underline'
                          : 'text-blue-600 hover:text-blue-800 hover:underline'
                      }`}
                    >
                      {announcement.contactPerson}
                    </button>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
